// Every item needed from Amazon on the top buildings, for Needed from Amazon: the items
// not received for stages still open (Step 3's, its open feedback points and Amazon's
// follow-up with the landlord among them), those never received for a stage already done,
// and those received; each under the Step 1 block it is for and its category there, with
// the date an open one is needed by and why (docs/Y2026-Q4/design-amazon-open-issues.md,
// design-landlord-follow-up-and-finished-stages.md, design-amazon-item-owners.md).
import type { AmazonBuilding, AmazonNeed, NeedPartial } from "../../api/hooks/useAmazonOverview";
import type { StoryStep } from "./blueprint";
import { day, daysBetween } from "./dates";
import { itemKeyOf } from "../../api/hooks/useAmazonOwners";
import { fromAmazon, owed, pointNeeds, stillNeeded } from "./needs";
import { byCommittedCod } from "./overrides";
import { STEPS } from "./pathSteps";
import {
  DEFAULT_BUFFER_DAYS,
  itemNeededBy,
  neededBy,
  neededByReason,
  programmeLinks,
} from "./schedule";

/** The Step 1 boxes whose items come first when needed-by dates tie, in this order; any
 * other box follows, in Step 1's order (Noaa, 19 Oct 2026). */
const BOX_ORDER = ["signoff", "llpack", "roof", "inputs", "design", "access2"];

/** Not received for a stage still open, not received for a stage already done, or
 * received (Noaa, 21 Oct 2026). */
export type ItemState = "open" | "finished" | "received";

export const ITEM_STATES: { state: ItemState; label: string }[] = [
  { state: "open", label: "Not received, open stages" },
  { state: "finished", label: "Not received, finished stages" },
  { state: "received", label: "Received" },
];

export interface ListedItem {
  /** Unique on its building. */
  key: string;
  /** What the item is owned and answered by (``itemKeyOf``). */
  itemKey: string;
  state: ItemState;
  /** The Step 1 block the item is for: its step's, or the box that step sits in. */
  block: string;
  /** Where it sits in that block's list; "" on a step whose list has none. */
  category: string;
  text: string;
  /** The contract or spec clauses behind it, as Step 1 lists them; "" where none is. */
  clause: string;
  /** An open one's date; null where the building has no committed COD to work back from. */
  neededBy: Date | null;
  /** Why it is needed by that date. */
  reason: string;
  /** Days past ``neededBy``; null while it is not yet due, and on any item not open. */
  daysLate: number | null;
  /** Where its box comes when needed-by dates tie (``BOX_ORDER``, then Step 1's order). */
  boxRank: number;
  /** Partially received: why, what we hold and what is still needed; null otherwise. */
  partial: NeedPartial | null;
  /** A finished one's stage, by its title on Step 1, and the day it was done. */
  stage: string;
  doneOn: Date | null;
  /** A received one's date and who gave it, as the record has them. */
  receivedOn: string;
  receivedFrom: string;
  /** The item itself, which a comment on its row names to its keeper (itemReviewAttrs). */
  need: AmazonNeed;
}

export interface SiteItems {
  building: AmazonBuilding;
  cod: Date | null;
  items: ListedItem[];
}

export type SortBy = "name" | "cod";

/** A partially received item whose grade the API sent no details of (an operator's
 * override, or an API deployed before them). */
const emptyPartial: NeedPartial = { gaps: [], held: [], to_provide: "", graded_on: "" };

export const byName = (a: AmazonBuilding, b: AmazonBuilding): number =>
  a.name.localeCompare(b.name, "en-GB", { numeric: true });

/** Every item needed from Amazon on each building, open ones earliest needed first
 * (undated last) then by box (``BOX_ORDER``) so a category's items sit together, then the
 * finished stages' and the received ones in Step 1's order; buildings with none are left
 * out. ``buildings`` are as the overview shows them, every override applied. */
export const neededItems = (
  buildings: AmazonBuilding[],
  steps: StoryStep[],
  sortBy: SortBy,
  now: Date,
): SiteItems[] => {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const order = new Map(steps.map((s, i) => [s.id, i]));
  /** The box Step 1 draws ``id`` in: the visible box it sits in, else its own step; a step
   * Step 1 never draws (an internal one) by the programme's own title. */
  const boxOf = (id: string): { id: string; title: string } => {
    const own = byId.get(id);
    if (!own) return { id, title: STEPS.find((s) => s.id === id)?.title ?? id };
    const box = own.inBox ? byId.get(own.inBox) : undefined;
    return box && !box.hidden ? box : own;
  };
  const at = (id: string): number => order.get(id) ?? Infinity;
  const rank = (box: string): number =>
    BOX_ORDER.includes(box) ? BOX_ORDER.indexOf(box) : BOX_ORDER.length + at(box);
  const links = programmeLinks(steps);
  const stateRank: Record<ItemState, number> = { open: 0, finished: 1, received: 2 };
  return buildings
    .map((b) => {
      const need = neededBy(b, links, DEFAULT_BUFFER_DAYS);
      const listed = (n: AmazonNeed, state: ItemState): ListedItem => {
        const by = state === "open" ? itemNeededBy(b, n, need) : null;
        const step = b.steps[n.step];
        return {
          key: n.point_id ? `point:${n.point_id}` : n.key,
          itemKey: itemKeyOf(n),
          state,
          block: boxOf(n.step).title,
          category: n.category ?? "",
          text: n.text,
          clause: n.clause,
          neededBy: by,
          reason: by ? neededByReason(n, (id) => byId.get(id)?.title ?? id) : "",
          daysLate: by && by < now ? daysBetween(by, now) : null,
          boxRank: rank(boxOf(n.step).id),
          partial: n.status === "partial" ? (n.partial ?? emptyPartial) : null,
          stage: byId.get(n.step)?.title ?? STEPS.find((s) => s.id === n.step)?.title ?? n.step,
          doneOn: state === "finished" && step?.date ? day(step.date) : null,
          receivedOn: state === "received" ? n.date : "",
          receivedFrom: state === "received" ? (n.supplied_by ?? "") : "",
          need: n,
        };
      };
      const own = (b.needs ?? []).filter(fromAmazon);
      const open = [
        ...own,
        ...pointNeeds(b, Object.keys(b.steps)),
        ...(b.landlord_follow_up ? [b.landlord_follow_up] : []),
      ]
        .filter((n) => fromAmazon(n) && stillNeeded(b, n))
        .map((n) => listed(n, "open"));
      const finished = own
        .filter((n) => owed(n) && b.steps[n.step]?.state === "done")
        .map((n) => listed(n, "finished"));
      const received = own.filter((n) => n.status === "received").map((n) => listed(n, "received"));
      const items = [...open, ...finished, ...received].sort(
        (x, y) =>
          stateRank[x.state] - stateRank[y.state] ||
          Number(!x.neededBy) - Number(!y.neededBy) ||
          (x.neededBy?.getTime() ?? 0) - (y.neededBy?.getTime() ?? 0) ||
          x.boxRank - y.boxRank,
      );
      return { building: b, cod: b.committed_cod ? day(b.committed_cod) : null, items };
    })
    .filter((s) => s.items.length > 0)
    .sort((x, y) =>
      sortBy === "cod" ? byCommittedCod(x.building, y.building) : byName(x.building, y.building),
    );
};

/** The blocks ``issues`` hold, in Step 1's order. */
export const blocksOf = (
  issues: { block: string }[],
  steps: StoryStep[],
): string[] => {
  const at = new Map(steps.map((s, i) => [s.title, i]));
  const held = new Set(issues.map((i) => i.block));
  return [...held].sort((x, y) => (at.get(x) ?? Infinity) - (at.get(y) ?? Infinity));
};

/** The categories ``issues`` hold, in order of the first block holding each. */
export const categoriesOf = (
  issues: { block: string; category: string }[],
  steps: StoryStep[],
): string[] => {
  const blocks = blocksOf(issues, steps);
  const first = (c: string) => Math.min(...issues.filter((i) => i.category === c).map((i) => blocks.indexOf(i.block)));
  const held = [...new Set(issues.map((i) => i.category).filter(Boolean))];
  return held.sort((x, y) => first(x) - first(y));
};
